/**
 * Maps the pack/entrance name stored in the database onto the display name used
 * in the ticket purchase flow.
 *
 * Matching is done on the components present in the stored name rather than on an
 * exact string, so it survives ordering changes, extra separators, accents and
 * casing (e.g. "Entrada General + Aviario" and "entrada general y aviario" both
 * resolve to "Experiencia Aviario"). Names with no rule fall through unchanged.
 *
 * Rules are evaluated in order, so the most specific combination must come first:
 * "Entrada General + Aviario + Jardín Botánico" contains every component of the
 * two-part rules as well, and would otherwise match one of those instead.
 */

interface DisplayNameRule {
  /** Every one of these must appear in the stored name for the rule to apply. */
  components: string[];
  displayName: string;
}

const displayNameRules: DisplayNameRule[] = [
  {
    components: ['entrada general', 'aviario', 'jardin botanico'],
    displayName: 'Experiencia Completa',
  },
  {
    components: ['entrada general', 'aviario'],
    displayName: 'Experiencia Aviario',
  },
  {
    components: ['entrada general', 'jardin botanico'],
    displayName: 'Experiencia Botanica',
  },
];

/** Lowercases, strips accents and collapses whitespace so rules match loosely. */
const normalize = (name: string): string =>
  name
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/\s+/g, ' ')
    .trim();

const findRule = (name: string): DisplayNameRule | undefined => {
  const normalizedName = normalize(name);
  return displayNameRules.find((rule) =>
    rule.components.every((component) => normalizedName.includes(component))
  );
};

/** True when the name maps onto the most complete pack. */
export const isFullExperience = (name?: string | null): boolean =>
  !!name && findRule(name)?.displayName === displayNameRules[0].displayName;

const getTicketDisplayName = (name?: string | null): string => {
  if (!name) {
    return '';
  }
  return findRule(name)?.displayName || name;
};

export default getTicketDisplayName;
